import { useState, useMemo } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Progress, ErrorPanel } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core/styles';
import {
  useOkrEntities,
  formatGroupName,
  ownerGroupSlug,
  OkrTreeNode,
} from '../../hooks/useOkrEntities';
import { CompanyOkrBanner } from '../CompanyOkrBanner';
import { VerticalCarousel } from '../VerticalCarousel';
import { OkrEmptyState } from '../OkrEmptyState';

const useStyles = makeStyles(theme => ({
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

function selectedObjId(node: OkrTreeNode, objIndex: number): string | undefined {
  return node.entity.spec.objectives[objIndex]?.id;
}

function linksToObj(node: OkrTreeNode, parentObjId: string | undefined): boolean {
  if (!parentObjId) return true;
  return node.entity.spec.objectives.some(obj => {
    if (!obj.parentObjRef) return false;
    return obj.parentObjRef.split('#')[1] === parentObjId;
  });
}

function filterByObj(nodes: OkrTreeNode[], parentObjId: string | undefined): OkrTreeNode[] {
  if (!parentObjId) return nodes;
  const linked = nodes.filter(n => linksToObj(n, parentObjId));
  return linked.length > 0 ? linked : nodes;
}

/** Derive a human-readable level label from tree depth */
function levelLabel(node: OkrTreeNode | undefined, fallback: string): string {
  if (!node) return fallback;
  switch (node.depth) {
    case 0: return 'Company';
    case 1: return 'BU';
    case 2: return 'Experience';
    default: return 'Team';
  }
}

/** Derive the focal entity's own level label */
function focalLevelLabel(depth: number): string {
  switch (depth) {
    case 0: return 'Company';
    case 1: return 'BU';
    case 2: return 'Experience';
    default: return 'Team';
  }
}

export function EntityGroupOkrTab() {
  const classes = useStyles();
  const { entity } = useEntity();
  const groupName = entity.metadata.name;

  const { loading, error, tree, quarters, selectedQuarter, setSelectedQuarter, groupLeads } = useOkrEntities();

  const [activeTab, setActiveTab] = useState(0);

  // Cascade tab state
  const [focalObjIndex, setFocalObjIndex] = useState(0);
  const [child1Index, setChild1Index] = useState(0);
  const [child1ObjIndex, setChild1ObjIndex] = useState(0);
  const [child2Index, setChild2Index] = useState(0);
  const [child2ObjIndex, setChild2ObjIndex] = useState(0);
  const [child3Index, setChild3Index] = useState(0);

  // All OKRs tab state
  const [all1Index, setAll1Index] = useState(0);
  const [all2Index, setAll2Index] = useState<number | null>(null);
  const [all3Index, setAll3Index] = useState(0);

  const focalNode = tree?.byOwner.get(groupName);
  const groupTitle = formatGroupName(groupName);
  const focalDepth = focalNode?.depth ?? 0;

  // Cascade: filter each level by the selected objective above
  const focalObjId = focalNode ? selectedObjId(focalNode, focalObjIndex) : undefined;

  const level1Nodes = useMemo(
    () => filterByObj(focalNode?.children ?? [], focalObjId),
    [focalNode, focalObjId],
  );

  const safeChild1 = Math.min(child1Index, Math.max(0, level1Nodes.length - 1));
  const selectedChild1 = level1Nodes[safeChild1];
  const child1ObjId = selectedChild1 ? selectedObjId(selectedChild1, child1ObjIndex) : undefined;

  const level2Nodes = useMemo(
    () => filterByObj(selectedChild1?.children ?? [], child1ObjId),
    [selectedChild1, child1ObjId],
  );

  const safeChild2 = Math.min(child2Index, Math.max(0, level2Nodes.length - 1));
  const selectedChild2 = level2Nodes[safeChild2];
  const child2ObjId = selectedChild2 ? selectedObjId(selectedChild2, child2ObjIndex) : undefined;

  const level3Nodes = useMemo(
    () => filterByObj(selectedChild2?.children ?? [], child2ObjId),
    [selectedChild2, child2ObjId],
  );

  // All OKRs: unfiltered, show everything below focal node
  const allLevel1 = focalNode?.children ?? [];
  const safeAll1 = Math.min(all1Index, Math.max(0, allLevel1.length - 1));
  const selectedAll1 = allLevel1[safeAll1];
  const allLevel2 = selectedAll1?.children ?? [];
  const safeAll2 = all2Index !== null
    ? Math.min(all2Index, Math.max(0, allLevel2.length - 1))
    : null;
  const selectedAll2 = safeAll2 !== null ? allLevel2[safeAll2] : null;
  const allLevel3 = selectedAll2
    ? selectedAll2.children
    : allLevel2.flatMap(n => n.children);

  function resetChildren() {
    setChild1Index(0); setChild1ObjIndex(0);
    setChild2Index(0); setChild2ObjIndex(0);
    setChild3Index(0);
    setAll1Index(0); setAll2Index(null); setAll3Index(0);
  }

  if (loading) return <Progress />;
  if (error) return <ErrorPanel error={error} />;

  return (
    <Box p={2}>
      {/* Controls row — same layout as cascade page */}
      <Box className={classes.controls}>
        {quarters.length > 0 && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel id="entity-okr-quarter-label">Quarter</InputLabel>
            <Select
              labelId="entity-okr-quarter-label"
              value={selectedQuarter}
              onChange={e => { setSelectedQuarter(e.target.value as string); resetChildren(); setFocalObjIndex(0); }}
              label="Quarter"
            >
              {quarters.map(q => (
                <MenuItem key={q} value={q}>{q}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {!focalNode ? (
        <OkrEmptyState />
      ) : (
        <>
          {/* Tabs — above everything, same position as cascade page */}
          {allLevel1.length > 0 && (
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              className={classes.tabs}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Cascade" />
              <Tab label="All OKRs" />
            </Tabs>
          )}

          {/* Focal entity banner — correct level label */}
          <CompanyOkrBanner
            node={focalNode}
            selectedIndex={focalObjIndex}
            onSelect={i => { setFocalObjIndex(i); resetChildren(); }}
            groupLeads={groupLeads}
            groupTitle={groupTitle}
            sectionLabel={focalLevelLabel(focalDepth)}
            sectionDesc={`${focalLevelLabel(focalDepth)} OKRs · ${groupTitle}`}
          />

          {activeTab === 0 ? (
            <>
              {/* Cascade — filtered by selected objective at each level */}
              {level1Nodes.length > 0 && (
                <VerticalCarousel
                  nodes={level1Nodes}
                  selectedIndex={safeChild1}
                  onSelect={i => { setChild1Index(i as number); setChild1ObjIndex(0); setChild2Index(0); setChild2ObjIndex(0); setChild3Index(0); }}
                  onObjChange={i => { setChild1ObjIndex(i); setChild2Index(0); setChild2ObjIndex(0); setChild3Index(0); }}
                  groupLeads={groupLeads}
                  sectionLabel={levelLabel(level1Nodes[0], 'Teams')}
                  sectionDesc={`Reporting into ${groupTitle} — select one to scope below`}
                  chipLabel={levelLabel(level1Nodes[0], 'Team')}
                />
              )}

              {level2Nodes.length > 0 && selectedChild1 && (
                <VerticalCarousel
                  nodes={level2Nodes}
                  selectedIndex={safeChild2}
                  onSelect={i => { setChild2Index(i as number); setChild2ObjIndex(0); setChild3Index(0); }}
                  onObjChange={i => { setChild2ObjIndex(i); setChild3Index(0); }}
                  groupLeads={groupLeads}
                  sectionLabel={levelLabel(level2Nodes[0], 'Teams')}
                  sectionDesc={`Within ${formatGroupName(ownerGroupSlug(selectedChild1.entity))}`}
                  chipLabel={levelLabel(level2Nodes[0], 'Team')}
                />
              )}

              {level3Nodes.length > 0 && selectedChild2 && (
                <VerticalCarousel
                  nodes={level3Nodes}
                  selectedIndex={Math.min(child3Index, Math.max(0, level3Nodes.length - 1))}
                  onSelect={i => setChild3Index(i as number)}
                  groupLeads={groupLeads}
                  sectionLabel="Team"
                  sectionDesc={`Within ${formatGroupName(ownerGroupSlug(selectedChild2.entity))}`}
                  chipLabel="Team"
                />
              )}
            </>
          ) : (
            <>
              {/* All OKRs — unfiltered, show all descendants */}
              {allLevel1.length > 0 && (
                <VerticalCarousel
                  nodes={allLevel1}
                  selectedIndex={safeAll1}
                  onSelect={i => { setAll1Index(i as number); setAll2Index(null); setAll3Index(0); }}
                  groupLeads={groupLeads}
                  sectionLabel={levelLabel(allLevel1[0], 'Teams')}
                  sectionDesc={`All direct reports of ${groupTitle}`}
                  chipLabel={levelLabel(allLevel1[0], 'Team')}
                />
              )}

              {allLevel2.length > 0 && selectedAll1 && (
                <VerticalCarousel
                  nodes={allLevel2}
                  selectedIndex={safeAll2}
                  onSelect={i => { setAll2Index(i); setAll3Index(0); }}
                  groupLeads={groupLeads}
                  sectionLabel={levelLabel(allLevel2[0], 'Teams')}
                  sectionDesc={`Within ${formatGroupName(ownerGroupSlug(selectedAll1.entity))} — click to filter teams, click again to show all`}
                  chipLabel={levelLabel(allLevel2[0], 'Team')}
                  toggleable
                />
              )}

              {allLevel3.length > 0 && (
                <VerticalCarousel
                  nodes={allLevel3}
                  selectedIndex={Math.min(all3Index, Math.max(0, allLevel3.length - 1))}
                  onSelect={i => setAll3Index(i as number)}
                  groupLeads={groupLeads}
                  sectionLabel="Team"
                  sectionDesc={
                    selectedAll2
                      ? `Within ${formatGroupName(ownerGroupSlug(selectedAll2.entity))}`
                      : `All teams under ${formatGroupName(ownerGroupSlug(selectedAll1.entity))}`
                  }
                  chipLabel="Team"
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
